import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { Button } from "@/components/ui/button";
import { 
  Mail, 
  MailPlus, 
  MailMinus, 
  Loader2,
  Check
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from '@/lib/utils';

interface QuickSubscribeButtonProps {
  assistantId: string;
  assistantName: string;
  currentUser: User | null;
  emailSubscriptions?: string[];
  variant?: 'default' | 'icon' | 'compact';
  onSubscriptionChange?: () => void;
  className?: string;
}

export function QuickSubscribeButton({
  assistantId,
  assistantName,
  currentUser,
  emailSubscriptions,
  variant = 'default',
  onSubscriptionChange,
  className
}: QuickSubscribeButtonProps) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (currentUser?.email && emailSubscriptions) {
      setIsSubscribed(emailSubscriptions.includes(currentUser.email));
    }
  }, [currentUser, emailSubscriptions]);

  const handleToggleSubscription = async () => {
    if (!currentUser?.email) return;

    setLoading(true);
    setShowSuccess(false);

    try {
      const endpoint = `/vac/subscriptions/${assistantId}`;
      const method = isSubscribed ? 'DELETE' : 'POST';
      
      const body = isSubscribed
        ? {
            endpoint,
            method,
            subscriber_email: currentUser.email
          }
        : {
            endpoint,
            method,
            subscriber_emails: [currentUser.email],
            requestor_email: currentUser.email
          };

      const response = await fetch('/api/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Subscription API response:', data);
        
        setIsSubscribed(!isSubscribed);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
        onSubscriptionChange?.();
      } else {
        const errorData = await response.json();
        console.error('Subscription API error:', errorData);
      }
    } catch (err) {
      console.error('Subscription toggle error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser?.email) {
    return null;
  }

  const buttonContent = () => {
    if (loading) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {variant !== 'icon' && <span className="ml-2">Processing...</span>}
        </>
      );
    }

    if (showSuccess) {
      return (
        <>
          <Check className="h-4 w-4 text-green-600" />
          {variant !== 'icon' && <span className="ml-2">Success!</span>}
        </>
      );
    }

    if (isSubscribed) {
      return (
        <>
          <MailMinus className="h-4 w-4" />
          {variant !== 'icon' && <span className="ml-2">Unsubscribe</span>}
        </>
      );
    }

    return (
      <>
        <MailPlus className="h-4 w-4" />
        {variant !== 'icon' && <span className="ml-2">Subscribe</span>}
      </>
    );
  };

  const button = (
    <Button
      onClick={handleToggleSubscription}
      disabled={loading}
      variant={isSubscribed ? "secondary" : "default"}
      size={variant === 'compact' ? 'sm' : 'default'}
      className={cn(
        variant === 'icon' && 'h-9 w-9 p-0',
        className
      )}
    >
      {buttonContent()}
    </Button>
  );

  if (variant === 'icon') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {button}
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isSubscribed 
                ? `Unsubscribe from ${assistantName} notifications` 
                : `Subscribe to ${assistantName} notifications`}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}